import { logger } from "@/utils/logger";
import {RootState, SummaryPanelState, SummaryRating} from "@/types";
import {Module} from "vuex";
import {getOffer} from "@/store/services/offerService";
import Notify from 'quasar/src/plugins/Notify.js';;
import {getOrderEditMode} from "@/store/services/order/orderService";
import {getSummaryParties} from "@/store/services/order/orderMapper";
import {
    asResourceUid,
    fetchSummaryRatings,
    mapAnalysisRatings,
    resolveAnalysisProcessIdFromOffer,
} from "@/store/services/summary/summaryRatingsService";

function resolveAnalysisProcessId(rootState: RootState, offer?: SummaryPanelState["offer"]): string | undefined {
    const taskVariables = rootState.taskModule?.selectedTask?.variables;
    const fromTask = asResourceUid(taskVariables?._decisionAnalysisProcessId)
        || asResourceUid(taskVariables?.analysisProcessId);
    if (fromTask) {
        return fromTask;
    }
    const fromAnalysis = asResourceUid(rootState.analysisModule?.analysis?.resourceUid)
        || asResourceUid(rootState.taskModule?.analysisModule?.analysis?.resourceUid);
    if (fromAnalysis) {
        return fromAnalysis;
    }
    const offerSource = offer
        || rootState.summaryPanelModule?.offer
        || rootState.taskModule?.offer
        || rootState.middleOfficeModule?.offer
        || rootState.demandModule?.offer;
    return asResourceUid((offerSource as { _decisionAnalysisProcessId?: unknown } | undefined)?._decisionAnalysisProcessId);
}

const summaryPanelModule: Module<SummaryPanelState, RootState> = {
    namespaced: true,
    state: {},
    getters: {
        getOrderParties(state) {
            const {order} = state
            return order ? getSummaryParties(order) : []
        },
        getAnalysisRatings(state) {
            return state.analysisRatings || []
        },
    },
    actions: {
        setOrder({commit}, payload) {
            const {orderId} = payload
            if (!orderId) {
                throw new Error('OrderId is required')
            }
            getOrderEditMode(orderId).then(order => {
                commit('setOrder', order)
                if (payload.callback) payload.callback(order)
            }).catch(err => {
                logger.error(err)
                Notify.create({
timeout: 10000,
            actions: [{ icon: 'close', color: 'white' }],
                    message: err.message,
                    color: 'negative'
                });
            })
        },
        setOfferFromTask({commit}, payload){
            const {offer} = payload
            commit('setOffer', offer)
            
        },
        setOffer({commit}, payload) {
            const offerId = payload.offer.resourceUid
            if (!offerId) {
                throw new Error('OfferId is required')
            }
            commit('setOffer', payload.offer)
        },
        async loadRatings({ commit, rootState, state }) {
            const offerSource = state.offer
                || rootState.summaryPanelModule?.offer
                || rootState.taskModule?.offer
                || rootState.middleOfficeModule?.offer
                || rootState.demandModule?.offer;
            let processId = resolveAnalysisProcessId(rootState, offerSource);
            if (!processId) {
                try {
                    processId = await resolveAnalysisProcessIdFromOffer(
                        offerSource,
                        rootState.middleOfficeModule?.decisionStep
                    );
                } catch (err) {
                    logger.error(err);
                }
            }
            const fallbackRatings = mapAnalysisRatings(
                rootState.analysisModule?.analysis?.analysisRatings
                || rootState.taskModule?.analysisModule?.analysis?.analysisRatings
            );

            if (!processId) {
                commit("setAnalysisRatings", { ratings: fallbackRatings, processId: undefined });
                return;
            }

            commit("setRatingsLoading", true);
            try {
                const ratings = await fetchSummaryRatings(processId);
                commit("setAnalysisRatings", { ratings, processId });
            } catch (err) {
                logger.error(err);
                commit("setAnalysisRatings", { ratings: fallbackRatings, processId });
            } finally {
                commit("setRatingsLoading", false);
            }
        },
        cleanState({ commit }) {
            commit("setInitial");
            this.dispatch('controlsModule/cleanState')
        },
        gatheringProcessOrder: ({ commit }, payload) => {
            const { request } = payload;
            commit("setGatheringProcessOrder", request.gatheringProcess);
        },
    },
    mutations: {
        setProposalScale(state, payload) {
            state.proposalScale = payload
        },
        setOffer(state, payload) {
            state.offer = payload
        },
        setOrder(state, payload) {
            state.order = payload
        },
        setInitial(state) {
            delete state.offer
            delete state.order
            delete state.analysisRatings
            delete state.analysisProcessId
            delete state.ratingsLoading
        },
        setGatheringProcessOrder(state, payload) {
            state.gatheringProcessOrder = payload;
        },
        setAnalysisRatings(state, payload: { ratings: SummaryRating[]; processId?: string }) {
            state.analysisRatings = payload.ratings;
            state.analysisProcessId = payload.processId;
        },
        setRatingsLoading(state, payload: boolean) {
            state.ratingsLoading = payload;
        },
    }
}

export default summaryPanelModule
